import React, { useState } from 'react';
import { ArrowUpRight, ChevronDown } from 'lucide-react';
import Swal from 'sweetalert2';
import contactIcon1 from '../../assets/img/contactus/1.png';
import contactIcon2 from '../../assets/img/contactus/2.png';
import contactIcon3 from '../../assets/img/contactus/3.png';
import contactIcon4 from '../../assets/img/contactus/4.png';

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    country: '',
    message: ''
  });

  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const payload = {
        ...formData,
        requirements: `Company: ${formData.company}\n\nMessage: ${formData.message}`
      };

      const response = await fetch('https://scantobimservice.us/api/send-email.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error(`Server error: ${response.status}`);

      const result = await response.json();

      if (result.status === 'success') {
        Swal.fire({ icon: 'success', title: 'Sent! 😊', text: 'We will contact you soon.' });
        setFormData({ name: '', email: '', phone: '', company: '', country: '', message: '' });
      } else {
        throw new Error(result.message || 'Submission failed');
      }
    } catch (error) {
      Swal.fire({ icon: 'error', title: 'Error ⚠️', text: error.message });
    } finally {
      setIsLoading(false);
    }
  };

  const stats = [
    {
      id: 1,
      value: "5+",
      label: "Years of experience",
      icon: <img src={contactIcon1.src || contactIcon1} alt="Experience" width={42} height={38} />
    },
    {
      id: 2,
      value: "50+",
      label: "In-house skilled talent",
      icon: <img src={contactIcon2.src || contactIcon2} alt="Talent" width={42} height={38} />
    },
    {
      id: 3,
      value: "499+",
      label: <>Projects delivered <br /> successfully</>,
      icon: < img src={contactIcon3.src || contactIcon3} alt="Projects" width={42} height={38} />
    },
    {
      id: 4,
      value: "95%",
      label: "Client Satisfaction Rate",
      icon: <img src={contactIcon4.src || contactIcon4} alt="Satisfaction" width={42} height={38} />
    }
  ];

  return (
    <section className="contact-section">
      <div className="contact-inner">
        {/* Left Column: Text + Stats Grid */}
        <div className="contact-left">
          <h2 className="contact-title">
            <span className="text-primary">Contact Us</span>
            <span className="text-white">For Project Discussion</span>
          </h2>
          <p className="contact-subtitle">
            Once you fill out this form, our sales representatives will contact you within 24 hours.
          </p>

          <div className="contact-stats">
            <div className="contact-stats-vertical-line"></div>
            <div className="contact-stats-horizontal-line"></div>
            {stats.map((stat) => (
              <div key={stat.id} className="contact-stat">
                <div className="contact-stat-icon">
                  {stat.icon}
                </div>
                <div className="contact-stat-value">{stat.value}</div>
                <div className="contact-stat-label">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Right Column: Contact Form */}
        <div className="contact-right">
          <div className="contact-form-wrapper">
            <form className="contact-form" onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label>Your Name</label>
                  <input type="text" name="name" placeholder="*Full Name" value={formData.name} onChange={handleChange} required />
                </div>
                <div className="form-group">
                  <label>Your Email</label>
                  <input type="email" name="email" placeholder="*Email Address" value={formData.email} onChange={handleChange} required />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <input type="tel" name="phone" placeholder="*Phone Number" value={formData.phone} onChange={handleChange} required />
                </div>
                <div className="form-group">
                  <input type="text" name="company" placeholder="Company Name" value={formData.company} onChange={handleChange} />
                </div>
              </div>
              <div className="form-group">
                <div className="select-wrapper">
                  <select name="country" value={formData.country} onChange={handleChange} required>
                    <option value="" disabled>Select Country</option>
                    {[
                      "Afghanistan", "Albania", "Algeria", "Andorra", "Angola", "Antigua and Barbuda", "Argentina", "Armenia", "Australia", "Austria", "Azerbaijan", "Bahamas", "Bahrain", "Bangladesh", "Barbados", "Belarus", "Belgium", "Belize", "Benin", "Bhutan", "Bolivia", "Bosnia and Herzegovina", "Botswana", "Brazil", "Brunei", "Bulgaria", "Burkina Faso", "Burundi", "Cabo Verde", "Cambodia", "Cameroon", "Canada", "Central African Republic", "Chad", "Chile", "China", "Colombia", "Comoros", "Congo", "Costa Rica", "Côte d'Ivoire", "Croatia", "Cuba", "Cyprus", "Czech Republic", "Denmark", "Djibouti", "Dominica", "Dominican Republic", "Ecuador", "Egypt", "El Salvador", "Equatorial Guinea", "Eritrea", "Estonia", "Eswatini", "Ethiopia", "Fiji", "Finland", "France", "Gabon", "Gambia", "Georgia", "Germany", "Ghana", "Greece", "Grenada", "Guatemala", "Guinea", "Guinea-Bissau", "Guyana", "Haiti", "Honduras", "Hungary", "Iceland", "India", "Indonesia", "Iran", "Iraq", "Ireland", "Israel", "Italy", "Jamaica", "Japan", "Jordan", "Kazakhstan", "Kenya", "Kiribati", "Korea (North)", "Korea (South)", "Kosovo", "Kuwait", "Kyrgyzstan", "Laos", "Latvia", "Lebanon", "Lesotho", "Liberia", "Libya", "Liechtenstein", "Lithuania", "Luxembourg", "Madagascar", "Malawi", "Malaysia", "Maldives", "Mali", "Malta", "Marshall Islands", "Mauritania", "Mauritius", "Mexico", "Micronesia", "Moldova", "Monaco", "Mongolia", "Montenegro", "Morocco", "Mozambique", "Myanmar", "Namibia", "Nauru", "Nepal", "Netherlands", "New Zealand", "Nicaragua", "Niger", "Nigeria", "North Macedonia", "Norway", "Oman", "Pakistan", "Palau", "Palestine", "Panama", "Papua New Guinea", "Paraguay", "Peru", "Philippines", "Poland", "Portugal", "Qatar", "Romania", "Russia", "Rwanda", "Saint Kitts and Nevis", "Saint Lucia", "Saint Vincent and the Grenadines", "Samoa", "San Marino", "Sao Tome and Principe", "Saudi Arabia", "Senegal", "Serbia", "Seychelles", "Sierra Leone", "Singapore", "Slovakia", "Slovenia", "Solomon Islands", "Somalia", "South Africa", "South Sudan", "Spain", "Sri Lanka", "Sudan", "Suriname", "Sweden", "Switzerland", "Syria", "Taiwan", "Tajikistan", "Tanzania", "Thailand", "Timor-Leste", "Togo", "Tonga", "Trinidad and Tobago", "Tunisia", "Turkey", "Turkmenistan", "Tuvalu", "Uganda", "Ukraine", "United Arab Emirates", "United Kingdom", "United States", "Uruguay", "Uzbekistan", "Vanuatu", "Vatican City", "Venezuela", "Vietnam", "Yemen", "Zambia", "Zimbabwe"
                    ].map(country => (
                      <option key={country} value={country}>{country}</option>
                    ))}
                  </select>
                  <ChevronDown size={16} className="select-icon text-primary" />
                </div>
              </div>
              <div className="form-group">
                <textarea name="message" placeholder="Write Message....." value={formData.message} onChange={handleChange} required></textarea>
              </div>

              <div className="form-footer">
                <label className="checkbox-label">
                  <input type="checkbox" required style={{ width: 'auto', marginRight: '8px' }} />
                  <span className="terms-text">
                    Accept terms and conditions from<br />
                    Graviton.
                  </span>
                </label>
                <button type="submit" className="contact-submit" disabled={isLoading}>
                  {isLoading ? 'Sending...' : <>Send Message <ArrowUpRight size={14} /></>}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
